import React, { useState, useRef, useEffect, useCallback } from "react";
import Switch from "react-switch";
import { FaCode, FaUndo, FaChevronDown, FaClone } from "react-icons/fa";
import { MdDescription, MdTag } from "react-icons/md";
import { useConfirmation } from "../common/ConfirmationContext";
import PromptWriter from "../prompts/PromptWriter";
import { useSkillsContext } from "../../hooks/useSkillsContext";

const SkillEditor = ({ item, onSave, onClose, onDelete, onReset }) => {
  const keyInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const { openConfirmation } = useConfirmation();
  const isCore = item.core;
  const isNew = (item?.id ?? null) === null;
  const { coreSkills, selectedCoreSkillKey, setSelectedCoreSkillKey } =
    useSkillsContext();

  useEffect(() => {
    // Initialize selectedCoreSkillKey when component mounts
    if (item && "overloads" in item && item.overloads) {
      setSelectedCoreSkillKey(item.overloads);
    }
  }, []);

  const [editedSkill, setEditedSkill] = useState({
    ...item,
    tags: item.tags?.filter((tag) => tag.toLowerCase() !== "core").sort(),
    preProcessing: Boolean(item.preProcessing),
    postProcessing: Boolean(item.postProcessing),
  });

  const handleEscapeKey = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [handleEscapeKey]);

  useEffect(() => {
    if (!isCore && keyInputRef.current) {
      keyInputRef.current.focus();
      keyInputRef.current.select();
    } else if (descriptionInputRef.current) {
      descriptionInputRef.current.focus();
      descriptionInputRef.current.select();
    }
  }, [isCore]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (
      !isCore ||
      (isCore && ["description", "tags", "example"].includes(name))
    ) {
      setEditedSkill((prev) => ({
        ...prev,
        [name]:
          name === "tags" ? value.split(",").map((tag) => tag.trim()) : value,
      }));
    }
  };

  const handleCheckboxChange = (name) => {
    setEditedSkill((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleCodeChange = (value) => {
    setEditedSkill((prev) => ({
      ...prev,
      example: value,
    }));
  };

  const handleCoreSkillChange = (e) => {
    setSelectedCoreSkillKey(e.target.value);
    setEditedSkill((prev) => ({
      ...prev,
      overloads: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(editedSkill);
  };

  const handleDeleteClick = () => {
    if (isCore) return; // Prevent deletion of core skills
    openConfirmation({
      title: "Delete Skill",
      message:
        "Are you sure you want to delete this skill? This action cannot be undone.",
      onConfirm: () => {
        onDelete(item.id);
        onClose();
      },
    });
  };

  const handleResetClick = () => {
    if (!isCore) return; // Only allow reset for core skills
    openConfirmation({
      title: "Reset Skill",
      message:
        "Are you sure you want to reset this skill to its original state? This will remove all customizations.",
      onConfirm: () => {
        onReset(item.id);
        onClose();
      },
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto">
      <div className="bg-gray-800 p-6 rounded-lg w-full max-w-4xl m-4">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <div className="relative">
                <FaCode className="absolute left-2 top-3 text-gray-400" />
                <input
                  ref={keyInputRef}
                  type="text"
                  name="key"
                  value={editedSkill.key}
                  onChange={handleChange}
                  className={`w-full pl-8 p-2 bg-gray-700 text-white rounded ${
                    isCore ? "cursor-not-allowed opacity-50" : ""
                  }`}
                  placeholder="Skill Name"
                  disabled={isCore}
                />
              </div>
            </div>
            <div className="col-span-2">
              {!isCore && (
                <div className="relative">
                  <FaClone className="absolute left-2 top-3 text-gray-400" />
                  <select
                    value={selectedCoreSkillKey ?? item.overloads ?? ""}
                    onChange={handleCoreSkillChange}
                    className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
                  >
                    <option value="" disabled>
                      Select Core Skill
                    </option>
                    {coreSkills.length > 0 &&
                      coreSkills.map((skill) => (
                        <option key={skill.id} value={skill.key}>
                          {skill.key}
                        </option>
                      ))}
                  </select>
                  <FaChevronDown className="absolute right-2 top-3 text-gray-400 pointer-events-none" />
                </div>
              )}
            </div>
            <div className="col-span-4">
              <div className="relative">
                <MdDescription className="absolute left-2 top-3 text-gray-400" />
                <textarea
                  ref={descriptionInputRef}
                  name="description"
                  value={editedSkill.description}
                  onChange={handleChange}
                  className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                  placeholder="Description"
                  rows="2"
                />
              </div>
            </div>
            <div className="col-span-4">
              <div className="relative flex items-center">
                {isCore && (
                  <span className="bg-indigo-600 text-white text-xs px-2 py-1 rounded-full mr-2">
                    Core
                  </span>
                )}
                <MdTag
                  className={`absolute ${
                    isCore ? "left-[3.7rem]" : "left-2"
                  } top-3 text-gray-400`}
                />
                <input
                  type="text"
                  name="tags"
                  value={editedSkill?.tags?.join(", ")}
                  onChange={handleChange}
                  className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                  placeholder="Tags (comma separated)"
                />
              </div>
            </div>
            <div className="col-span-4 flex items-center space-x-4">
              <div className="flex items-center">
                <span className="text-white mr-2">Pre-Processing</span>
                <Switch
                  checked={Boolean(editedSkill.preProcessing)}
                  onChange={() => handleCheckboxChange("preProcessing")}
                  onColor="#4299e1"
                  offColor="#718096"
                  height={20}
                  width={40}
                  disabled={isCore}
                />
              </div>
              <div className="flex items-center">
                <span className="text-white mr-2">Post-Processing</span>
                <Switch
                  checked={Boolean(editedSkill.postProcessing)}
                  onChange={() => handleCheckboxChange("postProcessing")}
                  onColor="#4299e1"
                  offColor="#718096"
                  height={20}
                  width={40}
                  disabled={isCore}
                />
              </div>
            </div>
            <div className="col-span-4 h-[20vh]">
              <h3
                className="text-gray-400 mb-2"
                title="Provide a sample of the expected output for this skill. This helps in testing, validation, and understanding the skill's intended behavior."
              >
                Example Output
              </h3>
              <PromptWriter
                value={editedSkill.example || " "}
                onChange={handleCodeChange}
                language="jinja"
                placeholder="Enter your code snippet here ..."
              />
            </div>
          </div>
          <div className="mt-8 pt-4 flex justify-between">
            {isCore ? (
              <button
                type="button"
                onClick={handleResetClick}
                className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-500 flex items-center"
              >
                <FaUndo className="mr-2" /> Reset to Original
              </button>
            ) : (
              <div>
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className={`bg-red-600 text-white px-4 py-2 rounded hover:bg-red-500 ${
                    isNew ? "hidden" : ""
                  }`}
                >
                  Delete
                </button>
              </div>
            )}
            <div>
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-600 text-white px-4 py-2 rounded mr-2 hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SkillEditor;
